import { LitElement, html, css, CSSResultGroup, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { PluginInputChangeHandler, PluginStepState } from '@ixit/dof-core';
import { measureKeysOf, measureLabel, measureValue } from '@ixit/dof-core';
// Side-effect import — registers the shared <ixit-rotary-tool-silhouette> family component.
// dof-core is @umbraco-cms-free, so this is safe in the standalone preview bundle too (see
// memory feedback_plugin_bundles_no_umbraco_imports).
import '@ixit/dof-core';

/** Server-echoed geometry for a pinnfräs item (camelCase JSON of EndmillItemDetail). */
interface EndmillItemDetail {
    /** Null when the field is empty: it has no value, and the read-out shows "–", never 0. */
    cuttingDiameter?: number | null;
    fluteLength?: number | null;
    shankDiameter?: number | null;
    overallLength?: number | null;
    fluteCount?: number | null;
    neckLength?: number | null;
    /** The empty fields the mill waits on, by their labels; null when it waits on nothing. */
    waitingOn?: string[] | null;
    endStyle?: string;
    cornerRadius?: number | null;
    totalSetupTime?: number;
    totalMachineTime?: number;
    /** Per-operation breakdown behind the two totals (fräs 02) — the totals alone cannot answer
     *  "why is this mill 14 minutes?", and an author who cannot see WHICH operation moved cannot
     *  tell a mis-authored formula from a big tool. Ships in both modes, like Stepdrill's; absent
     *  means the item has no authored operations. */
    operationResults?: Record<string, Record<string, number>>;
    /** The RESOLVED end (issue 06) — "Flat" | "CornerRadius", stamped server-side by
     *  EndmillItemDetail.WithResolvedEnd. Never re-derive it here: the style vocabulary has ONE
     *  owner (EndmillToolProfileProvider.ResolveEnd), and the copy that used to live in this file
     *  IS the defect this field exists to close. */
    endKind?: string;
    /** The radius the tool will actually be ground to — clamped to the cutting radius, 0 on a flat
     *  end. `cornerRadius` beside it is what the author typed. */
    effectiveCornerRadius?: number;
    /** An end style WAS authored and the leaf did not recognise it (almost always a typo). Blank
     *  and "plan" are recognised, so this is never true for a deliberately plain mill. */
    endStyleUnrecognized?: boolean;
}

interface EndmillItemState {
    alias: string;
    status?: string;
    errorMessage?: string;
    silhouetteSvg?: string;
    /** Named family projections (ItemState.ProjectionSvgs) — the mill binds `cutProfile`
     *  (spårprofil per svep, issue 05). */
    projectionSvgs?: Record<string, string>;
    detail?: EndmillItemDetail;
}

const fmt = (n?: number, dp = 2): string =>
    (n ?? 0).toLocaleString('sv-SE', { maximumFractionDigits: dp });

/** A measure's read-out: an empty field has no value, so "–", never 0. Zero stays a real answer. */
const shown = (n?: number | null, dp = 2): string => (n == null ? '–' : fmt(n, dp));

const joinNames = (names: string[]): string =>
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} och ${names[names.length - 1]}`;

/** Where the profile would be, while there is none: the fields the mill waits on, when it names them
 *  (endmill-preview.test.ts). */
export const endmillEmptyProfileText = (waitingOn?: string[] | null): string =>
    waitingOn?.length
        ? `Profil visas när ${joinNames(waitingOn)} är ${waitingOn.length === 1 ? 'ifylld' : 'ifyllda'}`
        : 'Profil visas när måtten är ifyllda';

/**
 * The end as it will be GROUND, always shown (issue 06). It makes the "Hörnradie (mm)" row's
 * absence legible instead of merely missing, and — with the suffix below — it is where an endStyle
 * the leaf did not recognise becomes visible at all. Reads the server's resolution; never repeats
 * it. `Plan` alone would leave a typo indistinguishable from a deliberately plain mill, which was
 * the half of this defect the resolved kind on its own does not fix.
 */
const endLabel = (d: EndmillItemDetail): string => {
    const kind = d.endKind === 'CornerRadius' ? 'Hörnradie' : 'Plan';
    return d.endStyleUnrecognized ? `${kind} — okänd ände-stil "${d.endStyle ?? ''}"` : kind;
};

/**
 * True when the ground radius is not the authored one — an authored radius larger than the cutting
 * radius is ground AT the cutting radius (the fillet sits at the outer edge). Showing 6 where the
 * author typed 20 would be a quieter version of the same lie, so the preview says both.
 */
const radiusWasClamped = (d: EndmillItemDetail): boolean =>
    d.endKind === 'CornerRadius'
    && Math.abs((d.cornerRadius ?? 0) - (d.effectiveCornerRadius ?? 0)) > 1e-9;

/**
 * End-user preview for a pinnfräs (end-mill) item — a PLAN or HÖRNRADIE (emr) end. Unlike the
 * stepdrill preview there are NO user-editable inputs: every dimension + antal skär + frigångslängd +
 * end-style + hörnradie is an AUTHOR formula, so the runtime evaluates them and echoes the geometry
 * back on item.detail. The preview shows that read-out (incl. the corner radius when the end
 * RESOLVES to one — issue 06; never merely when a radius was authored) plus the server-rendered
 * silhouette, and offers STEP export.
 *
 * The silhouette SVG is generated server-side (the shared RotaryToolSilhouetteVisualizer, which draws
 * both EndKind.Flat and EndKind.CornerRadius) and arrives on item.silhouetteSvg;
 * <ixit-rotary-tool-silhouette> displays it and — via projectionSvgs.cutProfile (issue 05) — offers
 * the verktyg|spårprofil toggle (the groove cross-section per sweep, with the honesty caption).
 * onInputChange is part of the renderer contract but unused here (nothing to edit).
 */
@customElement('ixit-endmill-preview')
export class IxitEndmillPreview extends LitElement {
    @property({ attribute: false }) item!: EndmillItemState;
    /** The consumption context, as dof-core declares it for every renderer. `customerMode` says the
     *  reader is an anonymous visitor on the public page — this leaf's own author affordances come
     *  off (see the export button in render). Imported rather than re-declared locally: a private
     *  literal drifts from the contract silently. `name` is host-supplied and not on the shared type. */
    @property({ attribute: false }) state!: Partial<PluginStepState> & { name?: string };
    @property({ attribute: false }) onInputChange!: PluginInputChangeHandler;

    /** Bubbling export request — preview-base owns the auth-gated POST + browser download.
     *  Unreachable in slutkund-läge: the button is not rendered, because the export route requires
     *  an Umbraco login and would answer a customer with a 401. */
    private _exportStep = () => {
        this.dispatchEvent(new CustomEvent('dof-export-request', {
            detail: { itemAlias: this.item.alias, format: 'step' },
            bubbles: true,
            composed: true,
        }));
    };

    static styles: CSSResultGroup = css`
        :host {
            container-type: inline-size;
            container-name: rotary-preview;
            display: block;
            margin-top: 12px;
        }
        .header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 8px;
        }
        .alias {
            font-size: 14px;
            font-weight: 600;
            color: var(--uui-color-text, #1f2937);
        }
        .preview-layout {
            display: flex;
            gap: 16px;
            align-items: flex-start;
        }
        .preview-body {
            flex: 1 1 0;
            min-width: 0;
            order: 0;
        }
        .specs {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px 16px;
            padding: 10px 12px;
            background: var(--uui-color-surface-alt, #f9fafb);
            border-radius: 4px;
            font-size: 13px;
        }
        .specs .label {
            display: block;
            font-size: 11px;
            color: var(--uui-color-text-alt, #6b7280);
            text-transform: uppercase;
            letter-spacing: 0.04em;
        }
        .specs strong {
            font-size: 15px;
            color: var(--uui-color-text, #1f2937);
        }
        .action-row {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            align-items: center;
            margin-top: 12px;
        }
        ixit-rotary-tool-silhouette {
            flex: 0 0 320px;
            align-self: stretch;
            display: flex;
            order: 1;
        }
        .error {
            color: var(--uui-color-danger, #dc2626);
            padding: 8px;
            border: 1px solid var(--uui-color-danger-emphasis, #fecaca);
            border-radius: 4px;
            margin-bottom: 8px;
        }
        /* 900 and not 700: the shared silhouette drops its height cap at 900 (see
           rotary-tool-silhouette.ts — the number is the family's, decided by the step drill's
           table), and a drawing that goes full-width while the layout is still two columns has no
           column to be full-width in. The two must name the same number, or the band between them
           gives the mill the centred sliver that rule exists to prevent. */
        .operations {
            margin-top: 10px;
            /* No hex fallback: a light literal would keep the declaration VALID against a
               dark ground and hide the failure. An alpha of the real text colour composites
               over whatever the theme puts behind it. */
            border-top: 1px solid color-mix(in srgb, var(--uui-color-text) 20%, transparent);
            padding-top: 8px;
        }
        .operations .op-head {
            font-size: 0.8rem;
            font-weight: 700;
            opacity: 0.75;
            margin-bottom: 4px;
        }
        .op-grid {
            font-size: 0.82rem;
        }
        .op-row {
            display: grid;
            /* Column count is DATA: one per measure, set by _operationRows on the grid and
               inherited here. The fallback is the pair, which is what every stored
               operation carries today. */
            grid-template-columns: var(--op-columns, 1fr auto auto);
            gap: 10px;
            padding: 2px 0;
        }
        .op-row > span {
            text-align: right;
            font-variant-numeric: tabular-nums;
        }
        .op-row > span:first-child {
            text-align: left;
        }
        .op-head-row > span {
            font-weight: 600;
            opacity: 0.7;
        }
        /* A zero row is a CONDITIONAL operation that did not apply (a plain mill's radieslip), not
           a broken formula. Dimming says "counted, cost nothing" — hiding it would make the list
           change shape between two mills and hide exactly the row an author is checking. */
        .operations tr.zero td {
            opacity: 0.45;
        }
        @container rotary-preview (max-width: 900px) {
            .preview-layout {
                flex-direction: column;
            }
            .preview-body {
                order: 1;
            }
            ixit-rotary-tool-silhouette {
                flex: 0 0 auto;
                align-self: stretch;
                order: 0;
            }
        }
    `;

    /**
     * The minutes broken down per operation (fräs 02). Nothing authored → nothing rendered, rather
     * than a table with no rows in it.
     */
    private _operationRows(d: EndmillItemDetail) {
        const ops = Object.entries(d.operationResults ?? {});
        if (ops.length === 0) return nothing;
        // A grid with table ROLES rather than <table> markup. Not a style preference: lit parses
        // each `.map()` result as its own template, and a template whose root is <tr> is not
        // well-formed HTML on its own — the parser hoists it out and lit's markers collapse onto one
        // element ("Detected duplicate attribute bindings"). Roles keep the semantics a real table
        // would have given a screen reader.
        // One column per MEASURE, drawn from the union across operations — the two reserved keys
        // first, then whatever the author added. The column count is therefore data, not markup,
        // so the grid template comes with it rather than being written into the stylesheet.
        const keys = measureKeysOf(d.operationResults);
        const template = `1fr ${keys.map(() => 'auto').join(' ')}`;

        return html`
            <div class="operations">
                <div class="op-head">Operationer</div>
                <div class="op-grid" role="table" aria-label="Operationer"
                     style="--op-columns: ${template}">
                    <div class="op-row op-head-row" role="row">
                        <span role="columnheader">Operation</span>
                        ${keys.map((k) => html`<span role="columnheader">${measureLabel(k)} (min)</span>`)}
                    </div>
                    ${ops.map(([name, r]) => {
                        // Dimmed only when the operation contributes nothing at all — with a third
                        // measure, "setup and machine are both zero" is no longer the same claim.
                        const zero = keys.every((k) => measureValue(r, k) === 0);
                        return html`
                            <div class="op-row ${zero ? 'zero' : ''}" role="row">
                                <span role="cell">${name}</span>
                                ${keys.map((k) => html`<span role="cell">${fmt(measureValue(r, k), 1)}</span>`)}
                            </div>`;
                    })}
                </div>
            </div>`;
    }

    render() {
        // Same Detail rule as Stepdrill (issue 09 UX fix): with a payload present the host
        // renders the error banner and this component keeps the editor on screen — the
        // error-only view is reserved for evaluator crashes that produced no state at all.
        if (this.item?.status === 'Error' && !this.item?.detail) {
            return html`<div class="error">${this.item.errorMessage ?? 'Pinnfräsen kunde inte utvärderas.'}</div>`;
        }
        const d = this.item?.detail;
        return html`
            <div class="header">
                <span class="alias">Pinnfräs · ${this.item?.alias}</span>
            </div>
            <div class="preview-layout">
                <div class="preview-body">
                    ${d ? html`
                        <div class="specs">
                            <div><span class="label">Skärdiameter (mm)</span><strong>${shown(d.cuttingDiameter)}</strong></div>
                            <div><span class="label">Skärlängd (mm)</span><strong>${shown(d.fluteLength)}</strong></div>
                            <div><span class="label">Skaftdiameter (mm)</span><strong>${shown(d.shankDiameter)}</strong></div>
                            <div><span class="label">Total längd (mm)</span><strong>${shown(d.overallLength)}</strong></div>
                            <div><span class="label">Antal skär</span><strong>${shown(d.fluteCount, 0)}</strong></div>
                            ${Number(d.neckLength ?? 0) > 0
                                ? html`<div><span class="label">Frigångslängd (mm)</span><strong>${shown(d.neckLength)}</strong></div>`
                                : nothing}
                            <div><span class="label">Ände</span><strong>${endLabel(d)}</strong></div>
                            ${d.endKind === 'CornerRadius'
                                ? html`<div><span class="label">Hörnradie (mm)</span><strong>${shown(d.cornerRadius == null ? null : d.effectiveCornerRadius)}${
                                    radiusWasClamped(d) ? html` <em>(begränsad från ${shown(d.cornerRadius)})</em>` : nothing
                                  }</strong></div>`
                                : nothing}
                            ${Number(d.totalSetupTime ?? 0) > 0 || Number(d.totalMachineTime ?? 0) > 0
                                ? html`
                                    <div><span class="label">Total setup-tid (min)</span><strong>${fmt(d.totalSetupTime, 1)}</strong></div>
                                    <div><span class="label">Total maskin-tid (min)</span><strong>${fmt(d.totalMachineTime, 1)}</strong></div>`
                                : nothing}
                        </div>
                        ${this._operationRows(d)}
                    ` : nothing}
                    ${this.state?.customerMode ? nothing : html`
                        <div class="action-row">
                            <uui-button
                                class="export-btn"
                                look="secondary"
                                label="⬇ STEP-fil"
                                title="Ladda ner 3D-modell (.stp) — kräver Umbraco-inloggning"
                                @click=${this._exportStep}
                            ></uui-button>
                        </div>
                    `}
                </div>
                <ixit-rotary-tool-silhouette
                    .svg=${this.item?.silhouetteSvg ?? ''}
                    .cutProfileSvg=${this.item?.projectionSvgs?.cutProfile ?? ''}
                    .emptyText=${endmillEmptyProfileText(d?.waitingOn)}
                ></ixit-rotary-tool-silhouette>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ixit-endmill-preview': IxitEndmillPreview;
    }
}
